import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {Inject, Injectable, inject} from '@angular/core';
import {NgxSpinnerService} from 'ngx-spinner';
import {Observable, throwError} from 'rxjs';
import {catchError, finalize, switchMap} from 'rxjs/operators';
import {SKIP_SPINNER} from '../tokens/spinner.token';
import {
  clearAuthTokenKeys,
  readAccessToken,
  readRefreshToken,
} from '../auth/auth-token.storage';

@Injectable()
export class APIInterceptor implements HttpInterceptor {
  private activeRequests = 0;
  private spinner = inject(NgxSpinnerService);

  constructor(
    @Inject('BASE_URL') public baseURL: string,
    @Inject('SCOPE') public scope: string,
  ) {}
  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const isAbsolute = req.url.startsWith('http');
    const isAssets =
      req.url.startsWith('/assets') &&
      !req.url.startsWith('/assets/domain') &&
      !req.url.startsWith('/assets/admin');
    const isRefreshCall = req.url.includes(
      `${this.baseURL}/auth/${this.scope}/refresh`,
    );

    // pega sinalizador do contexto
    const skipSpinner =
      req.context.get(SKIP_SPINNER) || isAssets || isAbsolute || isRefreshCall;

    if (!skipSpinner && this.activeRequests === 0) {
      void this.spinner.show();
    }
    if (!skipSpinner) this.activeRequests++;

    const token = readAccessToken();

    const requestToSend =
      isAssets || isAbsolute
        ? req
        : req.clone({
            url: `${this.baseURL}${req.url}`,
            setHeaders: token ? {Authorization: `Bearer ${token}`} : {},
          });

    return next.handle(requestToSend).pipe(
      catchError((error: HttpErrorResponse) => {
        const is401 = error.status === 401;
        const isRetry = requestToSend.headers.has('x-retry');
        const hasRefresh = !!readRefreshToken();

        if (is401 && !isRetry && hasRefresh && !isRefreshCall) {
          return this.handle401Error(requestToSend, next);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        if (!skipSpinner) this.finish();
      }),
    );
  }

  private finish() {
    this.activeRequests--;
    if (this.activeRequests === 0) {
      void this.spinner.hide();
    }
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const refreshToken = readRefreshToken();
    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token available.'));
    }

    return this.refreshAccessToken(refreshToken).pipe(
      switchMap((newToken: string) => {
        localStorage.setItem('accessToken', newToken);

        // Retry the original request with a new token
        const retryRequest = request.clone({
          setHeaders: {
            Authorization: `Bearer ${newToken}`,
            'x-retry': 'true', // Prevent infinite loops
          },
        });

        return next.handle(retryRequest);
      }),
      catchError(() => {
        this.logout();
        return throwError(() => new Error('Token refresh failed.'));
      }),
    );
  }

  private refreshAccessToken(refreshToken: string): Observable<string> {
    const url = `${this.baseURL}/auth/${this.scope}/refresh`; // Ensure the URL includes the base URL

    return new Observable(observer => {
      const body = {refreshToken};
      fetch(url, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(body),
      })
        .then(res => res.json())
        .then(data => {
          const {token, tokenExpiresIn} = data;
          localStorage.setItem('accessToken', token);
          localStorage.setItem(
            'accessTokenExpiresIn',
            String(Date.now() + tokenExpiresIn * 1000),
          );
          observer.next(token);
          observer.complete();
        })
        .catch(err => observer.error(err));
    });
  }

  private logout(): void {
    clearAuthTokenKeys();
    sessionStorage.clear();
  }
}
